// Page thumbnails of a Sample_Packet (blueprint-sets Req 10.1, 12.8; design "SetProposalReview").
// The packet is loaded once per review by `PacketDocument` from the proposal's `documentUrl` (the documents bucket,
// already in the CSP, so no new origin) with `useLoadedDocument`, and shared by every `PageThumbnails`: PDF pages
// through `react-pdf` `Page` at 96 px, TIFF pages drawn with `drawTiffPage`, and PNG/JPEG (one page) as an image.
// Each thumbnail is a button that opens the one Enlarged_Viewer of the review at that page (enlarge-preview-annotate
// Req 4), rendered from the same loaded packet, with no annotations (Req 4.5, 10.1).
import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Page } from 'react-pdf';
import Box from '@cloudscape-design/components/box';
import Spinner from '@cloudscape-design/components/spinner';

import { EnlargedViewer } from './EnlargedViewer';
import { useLoadedDocument, type DecodedTiff, type LoadedDocument } from './loadedDocument';
import { drawTiffPage } from './tiffPage';

export const THUMBNAIL_WIDTH = 96;
/** Thumbnails shown per list; the rest are summarized as "+N more pages". */
export const MAX_THUMBNAILS = 24;

interface PacketSource {
  url: string;
  contentType: string;
  name: string;
}

interface PacketContext {
  source: PacketSource;
  loaded: LoadedDocument;
  /** Marks this packet's thumbnail buttons, so focus can fall back to the first one (Req 3.3). */
  thumbId: string;
  /** Opens the Enlarged_Viewer at `page`; a no-op while one is open (Req 1.8). */
  open: (page: number, opener: HTMLElement) => void;
}

const PacketCtx = createContext<PacketContext | null>(null);

/** Packet page count: the PDF's page count, the TIFF's IFD count, or 1 for an image (Req 4.4). */
function pageCountOf(loaded: LoadedDocument): number {
  return loaded.kind === 'pdf' || loaded.kind === 'tiff' || loaded.kind === 'image' ? loaded.pageCount : 1;
}

/** Loads the packet once for the thumbnails below it. Renders its children at once, whatever the load state. */
export function PacketDocument({ url, contentType, name, children }: PacketSource & { children: ReactNode }) {
  const loaded = useLoadedDocument(url, contentType);
  const thumbId = useId();
  const [enlarged, setEnlarged] = useState<{ page: number; opener: HTMLElement } | null>(null);
  // Mirrors `enlarged` for the callbacks; written only by `open` and `onClose`, the sole setters.
  const enlargedRef = useRef(enlarged);
  // The opener to focus once the viewer has closed (set on close, consumed by the effect below).
  const restoreTo = useRef<HTMLElement | null>(null);

  const open = useCallback((page: number, opener: HTMLElement) => {
    if (enlargedRef.current) return;
    const next = { page, opener };
    enlargedRef.current = next;
    setEnlarged(next);
  }, []);

  const onClose = useCallback(() => {
    restoreTo.current = enlargedRef.current?.opener ?? null;
    enlargedRef.current = null;
    setEnlarged(null);
  }, []);

  // Focus returns to the opener thumbnail, or the first thumbnail if it is gone (Req 3.3, 4.8).
  useEffect(() => {
    if (enlarged || !restoreTo.current) return;
    const opener = restoreTo.current;
    restoreTo.current = null;
    const target = opener.isConnected
      ? opener
      : document.querySelector<HTMLElement>(`[data-packet-thumb="${CSS.escape(thumbId)}"]`);
    target?.focus();
  }, [enlarged, thumbId]);

  return (
    <PacketCtx.Provider value={{ source: { url, contentType, name }, loaded, thumbId, open }}>
      {children}
      {enlarged && (
        <EnlargedViewer
          name={name}
          loaded={loaded}
          pageCount={pageCountOf(loaded)}
          initialPage={enlarged.page}
          onClose={onClose}
        />
      )}
    </PacketCtx.Provider>
  );
}

function TiffThumb({ tiff, page, label }: { tiff: DecodedTiff; page: number; label: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    try {
      drawTiffPage(tiff, page, canvas);
    } catch {
      // Shown as the text fallback below; a page beyond the TIFF's IFDs or an undecodable one.
      queueMicrotask(() => setError(true));
    }
  }, [tiff, page]);
  if (error) return <Box variant="small">No preview</Box>;
  return (
    <canvas
      ref={ref}
      role="img"
      aria-label={label}
      style={{ display: 'block', width: THUMBNAIL_WIDTH, height: 'auto' }}
    />
  );
}

function Thumb({ page, loaded, name }: { page: number; loaded: LoadedDocument; name: string }) {
  const label = `${name}, page ${page}`;
  switch (loaded.kind) {
    case 'loading':
      return <Spinner />;
    case 'error':
      return <Box variant="small">No preview</Box>;
    case 'pdf':
      return (
        <span role="img" aria-label={label} style={{ display: 'block' }}>
          <Page
            pdf={loaded.pdf}
            pageNumber={page}
            width={THUMBNAIL_WIDTH}
            className="thumbnail-page"
            renderAnnotationLayer={false}
            renderTextLayer={false}
            loading={<Spinner />}
            error={<Box variant="small">No preview</Box>}
          />
        </span>
      );
    case 'tiff':
      return <TiffThumb tiff={loaded.tiff} page={page} label={label} />;
    case 'image':
      return page === 1 ? (
        <img src={loaded.url} alt={label} width={THUMBNAIL_WIDTH} style={{ display: 'block' }} />
      ) : (
        <Box variant="small">No preview</Box>
      );
  }
}

const thumbButtonStyle = {
  display: 'block',
  width: THUMBNAIL_WIDTH,
  margin: 0,
  padding: 0,
  border: 'none',
  background: 'none',
  font: 'inherit',
  color: 'inherit',
  textAlign: 'start',
} as const;

/**
 * Thumbnails of the given 1-based pages, each captioned "Page n" and named "Enlarge page n" (Req 4.1). Needs a
 * `PacketDocument` above it; none renders nothing. Disabled while the packet loads or after it fails (Req 4.6, 4.7).
 */
export function PageThumbnails({ pages }: { pages: number[] }) {
  const ctx = useContext(PacketCtx);
  if (!ctx || pages.length === 0) return null;
  const shown = pages.slice(0, MAX_THUMBNAILS);
  const disabled = ctx.loaded.kind === 'loading' || ctx.loaded.kind === 'error';
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}>
      {shown.map((p) => (
        <figure key={p} style={{ margin: 0, width: THUMBNAIL_WIDTH }}>
          <button
            type="button"
            aria-label={`Enlarge page ${p}`}
            data-packet-thumb={ctx.thumbId}
            disabled={disabled}
            style={{ ...thumbButtonStyle, cursor: disabled ? 'default' : 'zoom-in' }}
            onClick={(e) => ctx.open(p, e.currentTarget)}
          >
            <Thumb page={p} loaded={ctx.loaded} name={ctx.source.name} />
          </button>
          <figcaption>
            <Box variant="small">Page {p}</Box>
          </figcaption>
        </figure>
      ))}
      {pages.length > shown.length && <Box variant="small">+{pages.length - shown.length} more pages</Box>}
    </div>
  );
}
